import { Icon, type IconName } from "../icons";
import type { Conversation, ConversationReport, Visit, WorkspaceView } from "../types";

export function HomeView({
  conversations,
  visits,
  reports,
  remote,
  onNavigate,
  onLogin,
}: {
  conversations: Conversation[];
  visits: Visit[];
  reports: ConversationReport[];
  remote: boolean;
  onNavigate: (view: WorkspaceView) => void;
  onLogin: () => void;
}) {
  const commitments = conversations.flatMap((item) => item.commitments);
  const pending = commitments.filter((item) => item.status === "pending");
  const urgent = conversations.filter((item) => item.priority === "urgent" && item.status !== "resolved");
  const unanswered = conversations.filter((item) => item.unreadCount > 0);
  const latest = conversations.slice(0, 4);

  return (
    <div className="home-page">
      <section className="home-hero">
        <div className="home-hero-copy">
          <p className="section-kicker">CLARIDAD OPERACIONAL DESDE CADA MENSAJE</p>
          <h1>Que ningún acuerdo importante vuelva a perderse en un chat.</h1>
          <p className="hero-lead">
            Conversa Smart reúne conversaciones, detecta lo pendiente y lo convierte en tareas, visitas y reportes con respaldo en el mensaje original.
          </p>
          <div className="hero-actions">
            <button className="button primary large" onClick={() => onNavigate("import")} type="button">
              Importar primer chat <Icon name="arrow" size={18} />
            </button>
            <button className="button secondary large" onClick={() => onNavigate("conversations")} type="button">
              Revisar conversaciones
            </button>
          </div>
          <div className="trust-row">
            <span><Icon name="check" size={16} /> La IA propone, tu equipo valida</span>
            <span><Icon name="check" size={16} /> Trazabilidad por mensaje</span>
            <span><Icon name="check" size={16} /> Funciona antes de Meta</span>
          </div>
        </div>

        <div className="next-action-board">
          <div className="board-heading">
            <div><span className="eyebrow-badge">HOY</span><h2>Qué necesita atención</h2></div>
            <span className={`workspace-mode ${remote ? "connected" : "demo"}`}><i /> {remote ? "AWS CONECTADO" : "MODO DEMO"}</span>
          </div>
          <div className="board-list">
            <ActionRow icon="bell" tone="danger" value={urgent.length} label="casos urgentes" detail={urgent[0]?.customer ?? "Sin urgencias activas"} onClick={() => onNavigate("conversations")} />
            <ActionRow icon="chat" tone="info" value={unanswered.length} label="conversaciones sin leer" detail={unanswered[0]?.customer ?? "Bandeja al día"} onClick={() => onNavigate("conversations")} />
            <ActionRow icon="check" tone="warning" value={pending.length} label="compromisos pendientes" detail={pending[0]?.description ?? "Sin pendientes"} onClick={() => onNavigate("commitments")} />
            <ActionRow icon="visit" tone="success" value={visits.filter((item) => item.status === "scheduled").length} label="visitas agendadas" detail={visits[0]?.title ?? "Todavía no hay visitas"} onClick={() => onNavigate("visits")} />
          </div>
          <div className="board-footer"><Icon name="clock" size={16} /><span>Actualizado al abrir la plataforma</span></div>
        </div>
      </section>

      <section className="story-section">
        <div className="story-heading">
          <p className="section-kicker">UN PLAN SIMPLE</p>
          <h2>De la conversación a una acción verificable.</h2>
          <p>No necesitas conectar Meta para comprobar el flujo. Empieza con una exportación real y valida cada paso.</p>
        </div>
        <div className="plan-grid">
          <PlanCard number="01" icon="import" title="Importa" text="Carga un chat exportado o pega su contenido. La plataforma reconoce mensajes, participantes y fechas." />
          <PlanCard number="02" icon="sparkles" title="Revisa" text="Confirma el resumen, los temas y compromisos sugeridos. Cada acción conserva el mensaje que la originó." />
          <PlanCard number="03" icon="check" title="Ejecuta" text="Crea una visita, cierra un seguimiento o genera un reporte listo para revisar e imprimir." />
        </div>
      </section>

      <section className="summary-section">
        <div className="section-title-row">
          <div><p className="section-kicker">RESUMEN DEL ESPACIO</p><h2>Tu operación, sin ruido.</h2></div>
          {!remote ? <button className="text-link-button" onClick={onLogin} type="button">Ingresar como equipo <Icon name="arrow" size={16} /></button> : null}
        </div>
        <div className="summary-cards">
          <SummaryCard icon="chat" value={conversations.length} label="Conversaciones" hint={`${unanswered.length} requieren respuesta`} tone="teal" onClick={() => onNavigate("conversations")} />
          <SummaryCard icon="check" value={pending.length} label="Compromisos" hint={`${commitments.length - pending.length} completados`} tone="blue" onClick={() => onNavigate("commitments")} />
          <SummaryCard icon="visit" value={visits.length} label="Visitas" hint={`${visits.filter((item) => item.status === "draft").length} por coordinar`} tone="amber" onClick={() => onNavigate("visits")} />
          <SummaryCard icon="document" value={reports.length} label="Reportes" hint={`${reports.filter((item) => item.status === "draft").length} borradores`} tone="navy" onClick={() => onNavigate("reports")} />
        </div>
      </section>

      <section className="recent-section">
        <div className="section-title-row">
          <div><p className="section-kicker">ACTIVIDAD RECIENTE</p><h2>Conversaciones que conviene mirar.</h2></div>
          <button className="button secondary" onClick={() => onNavigate("conversations")} type="button">Ver bandeja completa</button>
        </div>
        <div className="recent-grid">
          {latest.map((conversation) => (
            <button className="recent-card" key={conversation.id} onClick={() => onNavigate("conversations")} type="button">
              <div className="recent-card-top"><span className={`priority-dot ${conversation.priority}`} /><span>{conversation.company}</span><time>{conversation.lastMessageAt}</time></div>
              <h3>{conversation.customer}</h3>
              <p>{conversation.site} · {conversation.summary}</p>
              <footer><span>{conversation.nextAction ?? conversation.commitments[0]?.description ?? "Revisar conversación"}</span><Icon name="arrow" size={17} /></footer>
            </button>
          ))}
          {!latest.length ? <div className="friendly-empty card-panel"><span><Icon name="chat" size={25} /></span><strong>Aún no hay conversaciones</strong><p>Importa un chat para comenzar.</p></div> : null}
        </div>
      </section>

      <section className="activation-section">
        <div className="activation-copy">
          <p className="section-kicker light">ANTES DE META</p>
          <h2>La plataforma puede quedar probada y ordenada antes de conectar el canal.</h2>
          <p>Diseño, importación, autenticación, seguimientos, visitas, reportes y almacenamiento AWS pueden validarse primero. Meta será el último paso de activación del flujo en tiempo real.</p>
          <button className="button light large" onClick={() => onNavigate("import")} type="button">Probar con un chat real <Icon name="arrow" size={18} /></button>
        </div>
        <div className="activation-checklist">
          {[
            [true, "Diseño amigable y responsive"],
            [true, "Importador de conversaciones exportadas"],
            [true, "Cognito y roles preparados"],
            [true, "Compromisos, visitas y reportes"],
            [remote, "Persistencia autenticada en AWS"],
            [false, "Conexión en tiempo real con Meta"],
          ].map(([done, label]) => (
            <div className={done ? "done" : "pending"} key={String(label)}><span>{done ? "✓" : ""}</span><strong>{label}</strong><small>{done ? "Listo" : "Pendiente"}</small></div>
          ))}
        </div>
      </section>
    </div>
  );
}

function ActionRow({ icon, tone, value, label, detail, onClick }: { icon: IconName; tone: string; value: number; label: string; detail: string; onClick: () => void }) {
  return <button className="action-row" onClick={onClick} type="button"><span className={`action-icon ${tone}`}><Icon name={icon} size={18} /></span><strong>{value}</strong><div><span>{label}</span><small>{detail}</small></div><Icon name="arrow" size={17} /></button>;
}

function PlanCard({ number, icon, title, text }: { number: string; icon: IconName; title: string; text: string }) {
  return <article className="plan-card"><div className="plan-card-top"><span>{number}</span><i><Icon name={icon} size={20} /></i></div><h3>{title}</h3><p>{text}</p></article>;
}

function SummaryCard({ icon, value, label, hint, tone, onClick }: { icon: IconName; value: number; label: string; hint: string; tone: string; onClick: () => void }) {
  return <button className={`summary-card ${tone}`} onClick={onClick} type="button"><span><Icon name={icon} size={20} /></span><strong>{value}</strong><h3>{label}</h3><p>{hint}</p><Icon name="arrow" size={18} /></button>;
}
